/**
 * Activity feed — live tail of events.
 *
 * Subscribes to the global event stream (SSE) and renders the rolling
 * buffer as a chronological list. New events animate in at the top.
 * Filter bar lets the operator narrow by kind / severity.
 *
 * This is "what's happening right now" — the page you keep open on a
 * second monitor while debugging a customer issue.
 *
 * Historical data (events older than the buffer cap, ~200) lives in
 * /workflow-executions and /messages — links at the top point to
 * those for deeper investigation.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  ArrowUp,
  Box,
  CheckSquare,
  ExternalLink,
  Inbox,
  Pause,
  Play,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { etlApi, type GlobalRunSummary } from "@/lib/api-etl";
import { messagesApi } from "@/lib/api-messages";
import { workflowExecutionsApi, type ExecutionSummary } from "@/lib/api-workflows";
import { usePermissions } from "@/lib/permissions";
import {
  useLiveEvents,
  type EventKind,
  type LiveEvent,
  type Severity,
} from "@/lib/eventStream";
import { cn } from "@/lib/cn";


// ---------------------------------------------------------------------------
// History → LiveEvent adapter
// ---------------------------------------------------------------------------

/** Convert a workflow_executions row to a LiveEvent so it slots into
 *  the same activity list as live SSE events. We synthesise a
 *  ``execution_finished`` event for any terminal status (and a
 *  ``execution_started`` for runs still running). The id mirrors the
 *  execution_id so a live update can dedupe-replace the history row. */
function execToEvent(e: ExecutionSummary): LiveEvent {
  const finished = e.status === "succeeded" || e.status === "failed" || e.status === "cancelled";
  const severity: Severity =
    e.status === "failed" ? "error" :
    e.status === "succeeded" ? "success" :
    "info";
  const duration_ms = e.started_at && e.finished_at
    ? new Date(e.finished_at).getTime() - new Date(e.started_at).getTime()
    : null;
  return {
    id: finished ? `exec:fin:${e.id}` : `exec:start:${e.id}`,
    kind: finished ? "execution_finished" : "execution_started",
    ts: e.finished_at ?? e.started_at,
    actor: "system",
    severity,
    title: `${e.workflow_name} ${e.status}`,
    subtitle: e.direction
      ? `${e.direction}${duration_ms !== null ? ` · ${duration_ms}ms` : ""}`
      : null,
    workflow_id: e.workflow_id,
    workflow_name: e.workflow_name,
    execution_id: e.id,
    direction: e.direction,
    status: e.status,
    duration_ms,
    error: e.error,
  };
}

/** Synthesise a feed row from a global ETL run. The id MUST match the LIVE
 * `EtlRunEvent` id scheme (`etl:fin:<run_id>` / `etl:start:<run_id>`, set in
 * `hub.etl.runner`) so live replaces the historical row instead of doubling. */
function etlRunToEvent(r: GlobalRunSummary): LiveEvent {
  const finished = r.status === "succeeded" || r.status === "failed" || r.status === "cancelled";
  const severity: Severity =
    r.status === "failed" ? "error" :
    r.status === "succeeded" ? (r.dead_lettered > 0 ? "warning" : "success") :
    "info";
  const duration_ms = r.started_at && r.finished_at
    ? new Date(r.finished_at).getTime() - new Date(r.started_at).getTime()
    : null;
  const dl = r.dead_lettered > 0 ? ` · ${r.dead_lettered} scarti` : "";
  return {
    id: finished ? `etl:fin:${r.id}` : `etl:start:${r.id}`,
    kind: finished ? "etl_run_finished" : "etl_run_started",
    ts: (r.finished_at ?? r.started_at) as string,
    actor: "system",
    severity,
    title: `ETL · ${r.pipeline_name || r.pipeline_id.slice(0, 8)} ${r.status}`,
    subtitle: `${r.rows_in}→${r.rows_out}${duration_ms !== null ? ` · ${duration_ms}ms` : ""}${dl}`,
    run_id: r.id,
    pipeline_id: r.pipeline_id,
    status: r.status,
    duration_ms,
  };
}


// ---------------------------------------------------------------------------
// Per-kind metadata
// ---------------------------------------------------------------------------

const KIND_ICON: Record<EventKind, LucideIcon> = {
  execution_started: Activity,
  execution_finished: WorkflowIcon,
  // Per-node lifecycle pings — high-volume during a run, so they get
  // a quieter glyph. Activity feed mostly cares about the *execution*
  // outcome; node-level events show up in the workflow editor canvas.
  node_started: Box,
  node_finished: CheckSquare,
  node_progress: Box,
  message_received: Inbox,
  workflow_enabled_changed: WorkflowIcon,
  notification_created: AlertCircle,
  // ETL runs (ADR-0038) — the started/finished bookends show in the feed;
  // the high-frequency metrics pings reuse the quiet node glyph.
  etl_run_started: Activity,
  etl_run_finished: CheckSquare,
  etl_run_metrics: Box,
};

const SEVERITY_TONE: Record<Severity, "neutral" | "active" | "warning" | "danger"> = {
  info: "neutral",
  success: "active",
  warning: "warning",
  error: "danger",
};


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ActivityPage() {
  const { t } = useTranslation();
  const { events, state } = useLiveEvents();
  const [paused, setPaused] = React.useState(false);
  // The ``ts`` of the newest event at the moment we paused. Used to
  // count how many new events have arrived since (for the "↑ N new"
  // catch-up chip — Slack/Twitter style). Empty when not paused.
  const [pausedAtTs, setPausedAtTs] = React.useState<string | null>(null);
  const [pausedSnapshot, setPausedSnapshot] = React.useState<LiveEvent[]>([]);
  const [severityFilter, setSeverityFilter] = React.useState<Severity | "all">("all");
  const [kindFilter, setKindFilter] = React.useState<EventKind | "all">("all");

  // ── History backfill ──────────────────────────────────────────────
  // The live event buffer is in-memory and starts empty on each page
  // load. To give the operator something to look at *right away* we
  // also pull the last N entries from the durable tables
  // (workflow_executions + messages) and synthesise LiveEvent rows
  // from them. They merge with the live stream by id (which is the
  // execution_id / message_id) so when a row transitions
  // started → finished, the live stream replaces the historical one.
  const execHistoryQ = useQuery({
    queryKey: ["activity", "history", "executions"],
    queryFn: () => workflowExecutionsApi.list({ page_size: 100 }),
    staleTime: 30_000,
  });
  const msgHistoryQ = useQuery({
    queryKey: ["activity", "history", "messages"],
    queryFn: () => messagesApi.list({ page_size: 50 }),
    staleTime: 30_000,
  });
  // ETL runs are activity too (the live `etl_run_*` events already flow through
  // the SSE stream; this backfills the recent history). Gated on `etl:read` —
  // a scoped operator never fetches it.
  const canEtl = usePermissions().can("etl", "read");
  const etlHistoryQ = useQuery({
    queryKey: ["activity", "history", "etl-runs"],
    queryFn: () => etlApi.allRuns({ page_size: 100 }),
    staleTime: 30_000,
    enabled: canEtl,
  });

  const history = React.useMemo<LiveEvent[]>(() => {
    const out: LiveEvent[] = [];
    for (const e of execHistoryQ.data?.items ?? []) {
      out.push(execToEvent(e));
    }
    for (const r of etlHistoryQ.data?.items ?? []) {
      // A pure-pending run has no timestamp yet (etl_runs has no created_at) —
      // skip it in the backfill; its live `etl_run_started` event surfaces it
      // the moment it starts.
      if (r.started_at || r.finished_at) out.push(etlRunToEvent(r));
    }
    for (const m of msgHistoryQ.data?.items ?? []) {
      out.push({
        id: `msg:${m.message_id}`,
        kind: "message_received",
        ts: m.received_at,
        actor: "system",
        severity: m.state === "FAILED" ? "error" : "info",
        title: `inbound · ${m.source}`,
        subtitle: m.canonical_type ?? m.correlation_id ?? null,
        message_id: m.message_id,
        source: m.source,
      });
    }
    return out;
  }, [execHistoryQ.data, msgHistoryQ.data, etlHistoryQ.data]);

  // Merge live + history, deduped by id, newest first. Live wins on
  // collision (it has the freshest state).
  const merged = React.useMemo<LiveEvent[]>(() => {
    const live = paused ? pausedSnapshot : events;
    const byId = new Map<string, LiveEvent>();
    for (const e of [...live, ...history]) {
      if (!byId.has(e.id)) byId.set(e.id, e);
    }
    return Array.from(byId.values()).sort((a, b) =>
      b.ts.localeCompare(a.ts),
    );
  }, [paused, pausedSnapshot, events, history]);

  const filtered = React.useMemo(() => {
    return merged.filter((e) => {
      if (severityFilter !== "all" && e.severity !== severityFilter) return false;
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      return true;
    });
  }, [merged, severityFilter, kindFilter]);

  function togglePause() {
    if (paused) {
      // Resume: blow away the snapshot and let live take over.
      setPaused(false);
      setPausedSnapshot([]);
      setPausedAtTs(null);
    } else {
      // Freeze: remember the current buffer + the newest ts so we
      // can count events that arrive after this moment.
      setPausedSnapshot(events);
      setPausedAtTs(events[0]?.ts ?? new Date().toISOString());
      setPaused(true);
    }
  }

  /** How many *new* events (post-pause) are sitting in the live buffer
   *  but hidden by the frozen snapshot. Capped at the buffer cap with
   *  ``+`` shown when we've definitely overflowed. */
  const newSincePause = React.useMemo(() => {
    if (!paused || !pausedAtTs) return 0;
    return events.filter((e) => e.ts > pausedAtTs).length;
  }, [paused, pausedAtTs, events]);
  // Buffer cap from eventStream.tsx is 200; we can't tell whether
  // events have dropped off the bottom, but if we hit the cap we
  // show ``200+`` to hint there might be more.
  const newSincePauseLabel =
    newSincePause >= 200 ? "200+" : String(newSincePause);

  function catchUp() {
    setPaused(false);
    setPausedSnapshot([]);
    setPausedAtTs(null);
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("activity.title", "Activity")}
        description={t(
          "activity.subtitle",
          "Live tail of platform events. Newest at the top. Buffer holds the last 200 events.",
        )}
        actions={
          <div className="flex items-center gap-3">
            <ConnectionDot state={state} />
            <Button
              size="icon"
              variant={paused ? "outline" : "ghost"}
              onClick={togglePause}
              aria-label={paused ? "Resume live updates" : "Pause live updates"}
              title={
                paused
                  ? "Resume — new events will appear at the top"
                  : "Pause — freeze the list so it stops scrolling while you read"
              }
            >
              {paused ? (
                <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
              ) : (
                <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
            </Button>
          </div>
        }
      />
      <PageContent>
        {/* Filter chips */}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t("activity.filter.severity", "Severity")}</span>
          <FilterChip
            active={severityFilter === "all"}
            onClick={() => setSeverityFilter("all")}
          >
            all
          </FilterChip>
          {(["error", "warning", "success", "info"] as const).map((s) => (
            <FilterChip
              key={s}
              active={severityFilter === s}
              onClick={() => setSeverityFilter(s)}
            >
              {s}
            </FilterChip>
          ))}
          <span className="ml-2 text-muted-foreground">
            {t("activity.filter.kind", "Kind")}
          </span>
          <FilterChip
            active={kindFilter === "all"}
            onClick={() => setKindFilter("all")}
          >
            all
          </FilterChip>
          {(["execution_started", "execution_finished", "message_received"] as const).map(
            (k) => (
              <FilterChip
                key={k}
                active={kindFilter === k}
                onClick={() => setKindFilter(k)}
              >
                {k}
              </FilterChip>
            ),
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={Activity}
            title={t("activity.empty", "Nothing here yet")}
            description={t(
              "activity.emptyDescription",
              state === "open"
                ? "Connected — events will appear here as workflows run."
                : "Connecting to the live stream…",
            )}
          />
        ) : (
          <div className="relative">
            {/* Catch-up chip — shown only while paused AND new events
                have arrived. Click → resume + jump to top. Sticky
                so it follows the scroll position when the user is
                deep in the list. */}
            {paused && newSincePause > 0 && (
              <div className="sticky top-0 z-10 mb-2 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    catchUp();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground shadow-lg",
                    "transition hover:opacity-90",
                  )}
                >
                  <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
                  {newSincePauseLabel} {t("activity.newEvents", "new")}
                </button>
              </div>
            )}
            <ul className="space-y-1">
              {filtered.map((e) => (
                <ActivityRow key={e.id} event={e} />
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 border-t border-border pt-3 text-xs text-muted-foreground">
          {t("activity.historyHint", "Looking for older events?")}{" "}
          <Link to="/workflow-runs" className="text-accent hover:underline">
            Workflow runs
          </Link>{" "}
          ·{" "}
          <Link to="/messages" className="text-accent hover:underline">
            Messages
          </Link>{" "}
          ·{" "}
          <Link to="/audit" className="text-accent hover:underline">
            Audit log
          </Link>
        </div>
      </PageContent>
    </PageContainer>
  );
}


// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ActivityRow({ event }: { event: LiveEvent }) {
  const Icon = KIND_ICON[event.kind] ?? Activity;
  const workflowId = typeof event.workflow_id === "string" ? event.workflow_id : null;
  const RowInner = (
    <>
      <Icon
        className={cn(
          "mt-0.5 h-3.5 w-3.5 shrink-0",
          event.severity === "error" ? "text-danger" :
          event.severity === "warning" ? "text-warning" :
          event.severity === "success" ? "text-success" :
          "text-muted-foreground",
        )}
        strokeWidth={1.5}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm">{event.title}</span>
          {event.severity === "error" && (
            <Badge tone="danger">{event.severity}</Badge>
          )}
        </div>
        {event.subtitle && (
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            {event.subtitle}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-mono">{new Date(event.ts).toLocaleTimeString()}</span>
        {workflowId && (
          <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
        )}
      </div>
    </>
  );

  const className = cn(
    "flex items-start gap-3 rounded-md border border-border/60 bg-surface px-3 py-2",
    "transition-colors",
    event.severity === "error" && "border-danger/30 bg-danger/5",
    SEVERITY_TONE[event.severity], // keeps the variable used by the type narrowing
  );

  if (workflowId) {
    return (
      <li>
        <Link to={`/workflows/${workflowId}`} className={cn(className, "hover:bg-muted/70")}>
          {RowInner}
        </Link>
      </li>
    );
  }
  return <li className={className}>{RowInner}</li>;
}


// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-6 items-center rounded-full px-2 font-mono text-[11px] transition",
        active
          ? "bg-accent/20 text-foreground"
          : "bg-muted text-muted-foreground hover:bg-muted/80",
      )}
    >
      {children}
    </button>
  );
}

function ConnectionDot({ state }: { state: "connecting" | "open" | "closed" }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          state === "open"
            ? "bg-success"
            : state === "connecting"
              ? "bg-warning animate-pulse"
              : "bg-muted-foreground/40",
        )}
      />
      <span className="font-mono">{state}</span>
    </span>
  );
}
